import React, { FC, useCallback, useMemo, useState } from 'react';
import { addDays, format, startOfDay, differenceInCalendarDays, getMonth, getYear } from 'date-fns';
import DatePicker from 'react-datepicker';
import { Container as CalendarContainer } from '@components/Calendar/components/Container';
import { IAvailableSlot } from '@api/appointments/types';
import { IVisitTemplate, IVisitSelection } from '../../types';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface IRescheduleSnapshot {
  visitSelections: Map<number, IVisitSelection>;
  slotsState: Map<number, IVisitSlotsState>;
}

interface ISlotPickerStepProps {
  visitTemplates: IVisitTemplate[];
  selectedVisitIndices: number[];
  visitSelections: Map<number, IVisitSelection>;
  onSlotSelect: (visitIndex: number, slot: IAvailableSlot, date: string) => void;
  onClearSlot: (visitIndex: number) => void;
  onFetchSlots: (visitIndex: number, date: string) => Promise<IAvailableSlot[]>;
  onRestoreSelections: (snapshot: Map<number, IVisitSelection>) => void;
}

interface IVisitSlotsState {
  loading: boolean;
  slots: IAvailableSlot[];
  selectedDate: string;
}

export const SlotPickerStep: FC<ISlotPickerStepProps> = ({
  visitTemplates,
  selectedVisitIndices,
  visitSelections,
  onSlotSelect,
  onClearSlot,
  onFetchSlots,
  onRestoreSelections,
}) => {
  const [slotsState, setSlotsState] = useState<Map<number, IVisitSlotsState>>(() => {
    const initial = new Map<number, IVisitSlotsState>();

    visitSelections.forEach((selection, visitIndex) => {
      if (selection.selectedSlot && selection.selectedDate) {
        initial.set(visitIndex, {
          loading: false,
          slots: [selection.selectedSlot],
          selectedDate: selection.selectedDate,
        });
      }
    });
    return initial;
  });

  const [autoPopulatingVisits, setAutoPopulatingVisits] = useState<Set<number>>(new Set());

  const [changeThisOnlyVisit, setChangeThisOnlyVisit] = useState<number | null>(null);

  const [rescheduleSnapshot, setRescheduleSnapshot] = useState<IRescheduleSnapshot | null>(null);

  const isVisitUnlocked = useCallback(
    (orderIndex: number) => {
      if (orderIndex === 0) return true;
      const prevVisitIndex = selectedVisitIndices[orderIndex - 1];

      return visitSelections.has(prevVisitIndex);
    },
    [selectedVisitIndices, visitSelections],
  );

  const getProtocolRange = useCallback(
    (orderIndex: number): { targetDate: Date; minDate: Date; maxDate: Date; window: number } | null => {
      if (orderIndex === 0) return null;

      const prevVisitIndex = selectedVisitIndices[orderIndex - 1];

      const prevSelection = visitSelections.get(prevVisitIndex);

      const currentVisit = visitTemplates.find((v) => v.index === selectedVisitIndices[orderIndex]);

      if (!prevSelection?.selectedDate || !currentVisit?.date) return null;

      const [year, month, day] = prevSelection.selectedDate.split('-');

      const prevDate = new Date(Number(year), Number(month) - 1, Number(day));

      const targetDate = addDays(prevDate, currentVisit.date);

      const windowDays = currentVisit.protocolWindow || 0;

      const windowMin = addDays(targetDate, -windowDays);

      const today = startOfDay(new Date());

      const minDate = windowMin < today ? today : windowMin;

      const maxDate = addDays(targetDate, windowDays);

      return { targetDate, minDate, maxDate, window: windowDays };
    },
    [selectedVisitIndices, visitSelections, visitTemplates],
  );

  const getMinDateForVisit = useCallback(
    (orderIndex: number): Date => {
      if (orderIndex === 0) return new Date();

      const prevVisitIndex = selectedVisitIndices[orderIndex - 1];

      const prevSelection = visitSelections.get(prevVisitIndex);

      if (!prevSelection?.selectedDate) return new Date();

      const currentVisit = visitTemplates.find((v) => v.index === selectedVisitIndices[orderIndex]);

      const [year, month, day] = prevSelection.selectedDate.split('-');

      const localDate = new Date(Number(year), Number(month) - 1, Number(day));

      const daysGap = currentVisit?.date || 1;

      const windowDays = currentVisit?.protocolWindow || 0;

      const earliest = addDays(localDate, daysGap - windowDays);

      const today = startOfDay(new Date());

      return earliest < today ? today : earliest;
    },
    [selectedVisitIndices, visitSelections, visitTemplates],
  );

  const clearVisitAndSubsequent = useCallback(
    (fromOrderIndex: number) => {
      const toRemove = selectedVisitIndices.slice(fromOrderIndex);

      toRemove.forEach((idx) => onClearSlot(idx));
      setSlotsState((prev) => {
        const next = new Map(prev);

        toRemove.forEach((idx) => next.delete(idx));
        return next;
      });
    },
    [selectedVisitIndices, onClearSlot],
  );

  const handleDateSelect = useCallback(
    async (visitIndex: number, dateStr: string) => {
      setSlotsState((prev) => {
        const next = new Map(prev);

        const existing = prev.get(visitIndex);

        next.set(visitIndex, {
          loading: true,
          slots: existing?.slots ?? [],
          selectedDate: dateStr,
        });
        return next;
      });

      const slots = await onFetchSlots(visitIndex, dateStr);

      setSlotsState((prev) => {
        const next = new Map(prev);

        next.set(visitIndex, { loading: false, slots, selectedDate: dateStr });
        return next;
      });
    },
    [onFetchSlots],
  );

  const MAX_SEARCH_DAYS = 31;

  const findEarliestSlot = useCallback(
    async (
      visitIndex: number,
      startDate: Date,
      attempt: number,
    ): Promise<{ slots: IAvailableSlot[]; dateStr: string } | null> => {
      if (attempt >= MAX_SEARCH_DAYS) return null;

      const candidateDate = addDays(startDate, attempt);

      const dateStr = format(candidateDate, 'yyyy-MM-dd');

      setSlotsState((prev) => {
        const next = new Map(prev);

        next.set(visitIndex, { loading: true, slots: [], selectedDate: dateStr });
        return next;
      });

      const slots = await onFetchSlots(visitIndex, dateStr);

      if (slots.length > 0) {
        setSlotsState((prev) => {
          const next = new Map(prev);

          next.set(visitIndex, { loading: false, slots, selectedDate: dateStr });
          return next;
        });
        return { slots, dateStr };
      }

      return findEarliestSlot(visitIndex, startDate, attempt + 1);
    },
    [onFetchSlots],
  );

  const autoPopulateVisit = useCallback(
    async (
      remainingIndices: number[],
      prevDate: string,
    ): Promise<void> => {
      if (remainingIndices.length === 0) {
        setAutoPopulatingVisits(new Set());
        return;
      }

      const [nextVisitIndex, ...rest] = remainingIndices;

      const nextVisit = visitTemplates.find((v) => v.index === nextVisitIndex);

      if (!nextVisit) {
        setAutoPopulatingVisits(new Set());
        return;
      }

      const daysGap = nextVisit.date || 1;

      const [year, month, day] = prevDate.split('-');

      const localDate = new Date(Number(year), Number(month) - 1, Number(day));

      const minDate = addDays(localDate, daysGap);

      const result = await findEarliestSlot(nextVisitIndex, minDate, 0);

      if (result) {
        onSlotSelect(nextVisitIndex, result.slots[0], result.dateStr);

        setAutoPopulatingVisits((prev) => {
          const updated = new Set(prev);

          updated.delete(nextVisitIndex);
          return updated;
        });

        await autoPopulateVisit(rest, result.dateStr);
        return;
      }

      setSlotsState((prev) => {
        const next = new Map(prev);

        next.set(nextVisitIndex, { loading: false, slots: [], selectedDate: format(minDate, 'yyyy-MM-dd') });
        return next;
      });

      setAutoPopulatingVisits(new Set());
    },
    [visitTemplates, onSlotSelect, findEarliestSlot],
  );

  const autoPopulateSubsequentVisits = useCallback(
    (afterOrderIndex: number, startingDate: string) => {
      const subsequentIndices = selectedVisitIndices.slice(afterOrderIndex + 1);

      if (subsequentIndices.length === 0) return;

      setAutoPopulatingVisits(new Set(subsequentIndices));

      autoPopulateVisit(subsequentIndices, startingDate);
    },
    [selectedVisitIndices, autoPopulateVisit],
  );

  const handleSlotClick = useCallback(
    (visitIndex: number, slot: IAvailableSlot) => {
      const state = slotsState.get(visitIndex);

      if (!state) return;

      const orderIndex = selectedVisitIndices.indexOf(visitIndex);

      onSlotSelect(visitIndex, slot, state.selectedDate);

      if (changeThisOnlyVisit === visitIndex) {
        setChangeThisOnlyVisit(null);
        setRescheduleSnapshot(null);
        return;
      }

      const subsequentIndices = selectedVisitIndices.slice(orderIndex + 1);

      if (subsequentIndices.length > 0) {
        subsequentIndices.forEach((idx) => onClearSlot(idx));

        setSlotsState((prev) => {
          const next = new Map(prev);

          subsequentIndices.forEach((idx) => next.delete(idx));
          return next;
        });

        autoPopulateSubsequentVisits(orderIndex, state.selectedDate);
      }
    },
    [slotsState, selectedVisitIndices, onSlotSelect, onClearSlot, autoPopulateSubsequentVisits, changeThisOnlyVisit],
  );

  const saveSnapshot = useCallback(() => {
    setRescheduleSnapshot({
      visitSelections: new Map(visitSelections),
      slotsState: new Map(slotsState),
    });
  }, [visitSelections, slotsState]);

  const handleCancelReschedule = useCallback(() => {
    if (!rescheduleSnapshot) return;

    onRestoreSelections(rescheduleSnapshot.visitSelections);
    setSlotsState(rescheduleSnapshot.slotsState);
    setRescheduleSnapshot(null);
    setChangeThisOnlyVisit(null);
  }, [rescheduleSnapshot, onRestoreSelections]);

  const handleChangeTime = useCallback(
    (visitIndex: number) => {
      saveSnapshot();

      const orderIndex = selectedVisitIndices.indexOf(visitIndex);

      clearVisitAndSubsequent(orderIndex);
    },
    [selectedVisitIndices, clearVisitAndSubsequent, saveSnapshot],
  );

  const handleChangeThisOnly = useCallback(
    (visitIndex: number) => {
      saveSnapshot();
      setChangeThisOnlyVisit(visitIndex);
      onClearSlot(visitIndex);
      setSlotsState((prev) => {
        const next = new Map(prev);

        next.delete(visitIndex);
        return next;
      });
    },
    [onClearSlot, saveSnapshot],
  );

  const getDayClassName = useCallback(
    (date: Date, orderIndex: number) => {
      const range = getProtocolRange(orderIndex);

      if (!range) return '';

      const d = startOfDay(date);

      const target = startOfDay(range.targetDate);

      const diff = differenceInCalendarDays(d, target);

      if (range.window > 0 && Math.abs(diff) <= range.window) return 'protocol-window-day';

      return '';
    },
    [getProtocolRange],
  );

  const protocolRanges = useMemo(() => {
    const map = new Map<number, ReturnType<typeof getProtocolRange>>();

    selectedVisitIndices.forEach((_, orderIndex) => {
      map.set(orderIndex, getProtocolRange(orderIndex));
    });
    return map;
  }, [selectedVisitIndices, getProtocolRange]);

  return (
    <>
      <div className="multi-appointment-info-box">
        Select a date and time slot for the first visit. Subsequent visits will be automatically
        scheduled to the earliest availability based on the required days between visits.
      </div>

      {selectedVisitIndices.map((visitIndex, orderIndex) => {
        const visit = visitTemplates.find((v) => v.index === visitIndex);

        if (!visit) return null;

        const unlocked = isVisitUnlocked(orderIndex);

        const selection = visitSelections.get(visitIndex);

        const hasSelection = !!selection?.selectedSlot;

        const state = slotsState.get(visitIndex);

        const effectiveDate = state?.selectedDate ?? selection?.selectedDate ?? '';

        const persistedSlots = selection?.selectedSlot ? [selection.selectedSlot] : [];

        const effectiveSlots = state ? state.slots : persistedSlots;

        const minDate = getMinDateForVisit(orderIndex);

        const range = protocolRanges.get(orderIndex);

        const hasProtocolWindow = !!range && range.window > 0;

        let cardClass = 'multi-appointment-slot-picker-visit';
        if (!unlocked) cardClass += ' multi-appointment-slot-picker-visit--locked';
        if (hasSelection) cardClass += ' multi-appointment-slot-picker-visit--selected';

        return (
          <React.Fragment key={visitIndex}>
            {orderIndex > 0 && (
              <div className="multi-appointment-connector">
                <div className="multi-appointment-connector-line" />
                {visit.date && (
                  <div className="multi-appointment-connector-badge">
                    {visit.date} days between visits
                    {visit.protocolWindow ? ` (± ${visit.protocolWindow} day window)` : ''}
                  </div>
                )}
              </div>
            )}

            <div className={cardClass}>
              <div className="multi-appointment-slot-picker-header">
                <div
                  className={`multi-appointment-slot-picker-icon${hasSelection ? ' multi-appointment-slot-picker-icon--selected' : ''}`}
                >
                  {hasSelection ? '✓' : `V${visit.index + 1}`}
                </div>
                <div className="multi-appointment-slot-picker-info">
                  <div className="multi-appointment-slot-picker-title">
                    Visit {visit.index + 1} — {visit.type || visit.name}{' '}
                    <span className="multi-appointment-badge multi-appointment-badge--info">
                      {visit.duration} min
                    </span>
                    {visit.md && (
                      <span
                        className="multi-appointment-badge multi-appointment-badge--info"
                        style={{ marginLeft: 4 }}
                      >
                        MD
                      </span>
                    )}
                  </div>
                  <div className="multi-appointment-slot-picker-meta">
                    {(() => {
                      if (!unlocked) {
                        const prevVisit = visitTemplates.find((v) => v.index === selectedVisitIndices[orderIndex - 1]);

                        return `Select Visit ${prevVisit ? prevVisit.index + 1 : orderIndex} first to unlock`;
                      }
                      if (hasSelection) {
                        const [year, month, day] = (selection?.selectedDate || '').split('-');

                        const localDate = new Date(Number(year), Number(month) - 1, Number(day));

                        return `Scheduled: ${selection?.selectedSlot?.display_from} — ${selection?.selectedSlot?.display_to} on ${format(localDate, 'MMM d, yyyy')}`;
                      }
                      return 'Select a date to view available time slots';
                    })()}
                  </div>
                </div>
              </div>

              {autoPopulatingVisits.has(visitIndex) && (
                <div className="multi-appointment-slot-picker-empty">
                  Auto-scheduling based on previous visit...
                </div>
              )}

              {unlocked && !hasSelection && !autoPopulatingVisits.has(visitIndex) && (
                <>
                  {rescheduleSnapshot && (
                    <div className="multi-appointment-cancel-row">
                      <button
                        type="button"
                        className="multi-appointment-change-button multi-appointment-change-button--secondary"
                        onClick={handleCancelReschedule}
                      >
                        Cancel Reschedule
                      </button>
                    </div>
                  )}

                  {range && (
                    <div className="multi-appointment-protocol-hint">
                      Target date: <strong>{format(range.targetDate, 'MMM d, yyyy')}</strong>
                      {hasProtocolWindow && (
                        <> — protocol window: {format(range.minDate, 'MMM d')} to {format(range.maxDate, 'MMM d, yyyy')}</>
                      )}
                    </div>
                  )}

                  <div className="multi-appointment-slot-picker-calendar">
                    <DatePicker
                      selected={effectiveDate ? (() => {
                        const [y, m, d] = effectiveDate.split('-');

                        return new Date(Number(y), Number(m) - 1, Number(d));
                      })() : null}
                      onChange={(date: Date | null) => {
                        if (date) handleDateSelect(visitIndex, format(date, 'yyyy-MM-dd'));
                      }}
                      minDate={minDate}
                      openToDate={range?.targetDate}
                      inline
                      calendarContainer={CalendarContainer}
                      formatWeekDay={(str: string) => str.slice(0, 3)}
                      dayClassName={(date: Date) => getDayClassName(date, orderIndex)}
                      renderCustomHeader={({
                        date: headerDate,
                        decreaseMonth,
                        increaseMonth,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled,
                      }) => (
                        <div className="multi-appointment-calendar-header">
                          <button
                            type="button"
                            className="multi-appointment-calendar-nav"
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                          >
                            ‹
                          </button>
                          <span className="multi-appointment-calendar-title">
                            {MONTH_LABELS[getMonth(headerDate)]} {getYear(headerDate)}
                          </span>
                          <button
                            type="button"
                            className="multi-appointment-calendar-nav"
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                          >
                            ›
                          </button>
                        </div>
                      )}
                    />
                  </div>

                  {effectiveDate && !state?.loading && (
                    <div className="multi-appointment-slot-picker-slots-count">
                      {effectiveSlots.length} slot{effectiveSlots.length !== 1 ? 's' : ''} available
                      for {(() => {
                        const [y, m, d] = effectiveDate.split('-');

                        return format(new Date(Number(y), Number(m) - 1, Number(d)), 'MMM d, yyyy');
                      })()}
                    </div>
                  )}

                  {state?.loading && (
                    <div className="multi-appointment-slot-picker-empty">
                      Loading available slots...
                    </div>
                  )}

                  {!state?.loading && effectiveDate && effectiveSlots.length === 0 && (
                    <div className="multi-appointment-slot-picker-empty">
                      No available slots for this date. Try selecting a different date.
                    </div>
                  )}

                  {!state?.loading && effectiveSlots.length > 0 && (
                    <div className="multi-appointment-slot-picker-slots">
                      {effectiveSlots.map((slot, slotIdx) => {
                        const isSelected =
                          selection?.selectedSlot?.from_time === slot.from_time &&
                          selection?.selectedSlot?.to_time === slot.to_time;

                        return (
                          <div
                            key={slotIdx}
                            className={`multi-appointment-slot-picker-slot${isSelected ? ' multi-appointment-slot-picker-slot--selected' : ''}`}
                            onClick={() => handleSlotClick(visitIndex, slot)}
                          >
                            <div className="multi-appointment-slot-picker-slot-time">
                              {slot.display_from} — {slot.display_to}
                            </div>
                            <div className="multi-appointment-slot-picker-slot-check">✓</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasSelection && (
                <div className="multi-appointment-change-buttons">
                  <button
                    className="multi-appointment-change-button"
                    onClick={() => handleChangeTime(visitIndex)}
                  >
                    Change &amp; Reschedule Following
                  </button>
                  <button
                    className="multi-appointment-change-button multi-appointment-change-button--secondary"
                    onClick={() => handleChangeThisOnly(visitIndex)}
                  >
                    Change This Only
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};
